import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Card,
  Form,
  Button,
  Alert,
  Row,
  Col,
} from "react-bootstrap";
import { createMeeting, getCommittees } from "../api/api";
import { useAuth } from "../context/AuthContext";
import toast from "../utils/toastUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import LotusIcon from "../components/LotusIcon";

const CreateMeeting = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [committees, setCommittees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    date: new Date().toISOString().split("T")[0],
    location: "",
    committee: "",
    notes: "",
  });

  useEffect(() => {
    // Fetch committees for the dropdown
    const fetchCommittees = async () => {
      try {
        const committeesData = await getCommittees();
        setCommittees(committeesData);
      } catch (err) {
        console.error("Error fetching committees:", err);
        setError("Failed to load committees. Please try again.");
      }
    };

    if (isAuthenticated) {
      fetchCommittees();
    }
  }, [isAuthenticated]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setError("Meeting title is required");
      return false;
    }
    if (!formData.date) {
      setError("Meeting date is required");
      return false;
    }
    if (!formData.committee) {
      setError("Please select a committee");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await createMeeting(formData);
      toast.success("Meeting created successfully!");
      navigate(`/meetings/${result._id}`);
    } catch (err) {
      console.error("Error creating meeting:", err);
      setError("Failed to create meeting. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <h1 className="mb-4">Create New Meeting</h1>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3" controlId="title">
              <Form.Label>Meeting Title</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter meeting title"
                required
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="date">
                  <Form.Label>Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="location">
                  <Form.Label>Location</Form.Label>
                  <Form.Control
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="Enter meeting location"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3" controlId="committee">
              <Form.Label>Committee</Form.Label>
              <Form.Select
                name="committee"
                value={formData.committee}
                onChange={handleChange}
                required
              >
                <option value="">Select Committee</option>
                {committees.map((committee) => (
                  <option key={committee._id} value={committee._id}>
                    {committee.name}
                  </option>
                ))}
                {committees.length === 0 && (
                  <>
                    <option value="PR">PR</option>
                    <option value="Operations">Operations</option>
                    <option value="Internal">Internal</option>
                    <option value="External">External</option>
                    <option value="SASComm">SASComm (All Committees)</option>
                  </>
                )}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-4" controlId="notes">
              <Form.Label>Initial Notes (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Enter any initial notes for the meeting"
              />
            </Form.Group>

            <div className="d-flex justify-content-end">
              <Button
                variant="secondary"
                onClick={() => navigate("/meetings")}
                className="me-2"
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? "Creating..." : "Create Meeting"}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default CreateMeeting;
